import { SITE_TODAY, matchesAudience, works as allWorks } from "@/lib/catalog";
import { getCandidatesFor, getIndexedWork, getWorkTimes } from "@/lib/catalogIndex";
import { computeFacets, matchesPriceBand, withinDays, type FacetCounts } from "@/lib/facets";
import { normalizeText, splitQuery } from "@/lib/normalize";
import { isUpcoming } from "@/lib/releaseDate";
import { workPath } from "@/lib/workIdentity";
import { getGenreById, getGenreName, getGenresForAudience, getWorkGenres, resolveGenreIds } from "@/lib/taxonomy";
import type { AudienceType, SortKey, Work, WorkFilters } from "@/types/work";
import type { RankingType, SearchCorrection, Suggestion } from "@/types/catalog";

export { normalizeText, splitQuery };
export * from "@/lib/searchParams";
export type { FacetCounts } from "@/lib/facets";
export type { RankingType, Suggestion, SearchCorrection } from "@/types/catalog";

/**
 * 検索エンジン本体（現在はデモデータ用）。
 *
 * スコアリングは正規化済みの索引（catalogIndex）を使い、クエリごとの再正規化を避ける。
 * 本番では同等の処理をサーバー側（DB / 検索API）で行い、この関数は MockCatalogRepository
 * の内部実装としてのみ使う。
 */

/** 検索スコアを返す。0 は不一致。作品ID完全一致を最優先。 */
export function scoreWork(work: Work, rawQuery: string): number {
  const tokens = splitQuery(rawQuery);
  if (tokens.length === 0) return 0;
  const normalizedQuery = normalizeText(rawQuery);
  const indexed = getIndexedWork(work.id);
  const normalizedId = indexed?.idKey ?? normalizeText(work.id);
  if (normalizedId === normalizedQuery) return 1000000;

  const candidates = getCandidatesFor(work);
  let total = 0;

  for (const token of tokens) {
    let best = 0;
    if (normalizedId === token) best = Math.max(best, 5000);
    else if (normalizedId.startsWith(token)) best = Math.max(best, 900);
    for (const candidate of candidates) {
      if (!candidate.value) continue;
      if (candidate.value === token) best = Math.max(best, candidate.weight);
      else if (candidate.value.startsWith(token)) best = Math.max(best, candidate.weight * 0.7);
      else if (candidate.value.includes(token)) best = Math.max(best, candidate.weight * 0.45);
    }
    if (best === 0) return 0;
    total += best;
  }

  const normalizedTitle = indexed?.normalizedTitle ?? normalizeText(work.title);
  if (normalizedTitle === normalizedQuery) total += 4000;
  else if (normalizedTitle.startsWith(normalizedQuery)) total += 1500;
  else if (normalizedTitle.includes(normalizedQuery)) total += 700;

  const normalizedAlternatives = indexed?.normalizedAlternatives ?? work.alternativeTitles.map(normalizeText);
  if (normalizedAlternatives.some((title) => title === normalizedQuery || title.includes(normalizedQuery))) {
    total += 500;
  }

  return total;
}

export function applyFilters(pool: Work[], filters: WorkFilters, mode: AudienceType): Work[] {
  const personToken = normalizeText(filters.person);
  const genreIds = resolveGenreIds(filters.genres);

  return pool.filter((work) => {
    // 未分類の取り込みデータは、明示的に選んだときだけ表示する
    if (filters.audience === "all") {
      if (!matchesAudience(work, mode)) return false;
    } else if (!work.audienceIds.includes(filters.audience)) {
      return false;
    }
    if (filters.types.length > 0 && !filters.types.includes(work.contentType)) return false;
    if (genreIds.length > 0) {
      const hit =
        filters.genreMatch === "and"
          ? genreIds.every((id) => work.genreIds.includes(id))
          : genreIds.some((id) => work.genreIds.includes(id));
      if (!hit) return false;
    }
    if (personToken) {
      const personHit =
        normalizeText(work.creator).includes(personToken) ||
        work.performers.some((performer) => normalizeText(performer).includes(personToken));
      if (!personHit) return false;
    }
    if (filters.makers.length > 0 && !filters.makers.includes(work.maker)) return false;
    if (filters.releaseStatus === "upcoming" && !isUpcoming(work.releaseDate, SITE_TODAY)) return false;
    if (filters.releaseStatus === "released" && isUpcoming(work.releaseDate, SITE_TODAY)) return false;
    if (filters.days !== null && !withinDays(work.releaseDate, filters.days)) return false;
    if (filters.priceBands.length > 0 && !filters.priceBands.some((band) => matchesPriceBand(work.price, band)))
      return false;
    if (filters.minRating !== null && (work.rating === null || work.rating < filters.minRating)) return false;
    if (filters.video === "yes" && !work.sampleVideoUrl) return false;
    if (filters.video === "no" && work.sampleVideoUrl) return false;
    if (filters.sale === "yes" && !work.onSale) return false;
    if (filters.sale === "no" && work.onSale) return false;
    if (filters.series === "single" && work.series) return false;
    if (filters.series === "series" && !work.series) return false;
    if (!matchesAttributeFilters(work, filters)) return false;
    return true;
  });
}

function compareStable(a: Work, b: Work): number {
  return a.id.localeCompare(b.id);
}

/** 属性値（"118" / "118分" など）を数値として読む */
function readAttributeNumber(work: Work, attributeId: string): number | null {
  const entry = work.attributes.find((attribute) => attribute.attributeId === attributeId);
  if (!entry) return null;
  const parsed = Number.parseFloat(entry.value.replace(/[^0-9.]/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
}

/** 選択中の商品形式に応じた属性レンジ条件（値のない作品は該当しない） */
function matchesAttributeFilters(work: Work, filters: WorkFilters): boolean {
  if (filters.attributes.length === 0) return true;
  return filters.attributes.every((filter) => {
    const value = readAttributeNumber(work, filter.attributeId);
    if (value === null) return false;
    if (filter.min !== null && value < filter.min) return false;
    if (filter.max !== null && value > filter.max) return false;
    return true;
  });
}

/** ソート前にタイムスタンプを1回だけ取り出す（比較関数内でのDateパースを避ける） */
function buildTimeMap(list: Work[]): Record<"release" | "added" | "updated", Map<string, number>> {
  const release = new Map<string, number>();
  const added = new Map<string, number>();
  const updated = new Map<string, number>();
  list.forEach((work) => {
    const times = getWorkTimes(work);
    release.set(work.id, times.release);
    added.set(work.id, times.added);
    updated.set(work.id, times.updated);
  });
  return { release, added, updated };
}

/** 並び替え。同点時は作品IDで安定化し、毎回同じ順序になるようにする。 */
export function sortWorks(list: Work[], sort: SortKey): Work[] {
  const copy = list.slice();
  switch (sort) {
    case "popular": {
      const scored = copy
        .filter((work) => work.rankingScore !== null)
        .sort(
          (a, b) =>
            (b.rankingScore ?? 0) - (a.rankingScore ?? 0) || compareStable(a, b),
        );
      const rest = copy.filter((work) => work.rankingScore === null).sort(compareStable);
      return [...scored, ...rest];
    }
    case "newest": {
      const times = buildTimeMap(copy).release;
      return copy.sort((a, b) => (times.get(b.id) ?? 0) - (times.get(a.id) ?? 0) || compareStable(a, b));
    }
    case "oldest": {
      const times = buildTimeMap(copy).release;
      return copy.sort((a, b) => (times.get(a.id) ?? 0) - (times.get(b.id) ?? 0) || compareStable(a, b));
    }
    case "priceAsc":
      return copy.sort((a, b) => a.price - b.price || compareStable(a, b));
    case "priceDesc":
      return copy.sort((a, b) => b.price - a.price || compareStable(a, b));
    case "rating": {
      const rated = copy
        .filter((work) => work.rating !== null)
        .sort(
          (a, b) =>
            (b.rating ?? 0) - (a.rating ?? 0) ||
            (b.reviewCount ?? 0) - (a.reviewCount ?? 0) ||
            compareStable(a, b),
        );
      const unrated = copy.filter((work) => work.rating === null).sort(compareStable);
      return [...rated, ...unrated];
    }
    default:
      return copy;
  }
}

export function searchWorks(
  filters: WorkFilters,
  sort: SortKey,
  mode: AudienceType,
  pool: Work[] = allWorks,
): Work[] {
  let result = applyFilters(pool, filters, mode);
  const query = filters.q.trim();

  if (query) {
    const scored = result
      .map((work) => ({ work, score: scoreWork(work, query) }))
      .filter((entry) => entry.score > 0);
    if (sort === "relevance") {
      return scored
        .sort((a, b) => b.score - a.score || compareStable(a.work, b.work))
        .map((entry) => entry.work);
    }
    result = scored.map((entry) => entry.work);
  }

  if (sort === "relevance") {
    return sortWorks(result, "newest");
  }
  return sortWorks(result, sort);
}

/** 実際の検索結果に応じたファセット件数（各ファセットは自分自身の条件を外して集計） */
export function getFacetCounts(
  filters: WorkFilters,
  mode: AudienceType,
  pool: Work[] = allWorks,
): FacetCounts {
  return computeFacets(pool, filters, mode);
}

export function getSuggestionsFromPool(
  query: string,
  mode: AudienceType,
  pool: Work[],
  limit = 8,
): Suggestion[] {
  const trimmed = query.trim();
  if (!trimmed) return [];
  const normalized = normalizeText(trimmed);

  const workSuggestions: Suggestion[] = pool
    .map((work) => ({ work, score: scoreWork(work, trimmed) }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || a.work.id.localeCompare(b.work.id))
    .slice(0, 5)
    .map(({ work }) => ({
      type: normalizeText(work.id) === normalized ? "id" : "work",
      label: work.title,
      sub: `${work.id}・${work.creator}・${getWorkGenres(work)
        .slice(0, 2)
        .map((genre) => genre.name)
        .join(" / ")}`,
      href: workPath(work.id),
    }));

  const people = new Map<string, string>();
  pool.forEach((work) => {
    if (normalizeText(work.creator).includes(normalized)) people.set(work.creator, "作者");
    work.performers.forEach((performer) => {
      if (normalizeText(performer).includes(normalized)) people.set(performer, "出演者");
    });
  });
  const personSuggestions: Suggestion[] = Array.from(people.entries())
    .slice(0, 3)
    .map(([name, role]) => ({
      type: "person",
      label: name,
      sub: `${role}で絞り込む`,
      href: `/search?person=${encodeURIComponent(name)}`,
    }));

  const genreCounter = new Map<string, number>();
  pool.forEach((work) => {
    work.genreIds.forEach((id) => {
      const genre = getGenreById(id);
      if (!genre) return;
      if (
        normalizeText(genre.name).includes(normalized) ||
        normalizeText(genre.kana).includes(normalized) ||
        genre.aliases.some((alias) => normalizeText(alias).includes(normalized))
      ) {
        genreCounter.set(genre.id, (genreCounter.get(genre.id) ?? 0) + 1);
      }
    });
  });
  const genreSuggestions: Suggestion[] = Array.from(genreCounter.entries())
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, 4)
    .map(([id, count]) => ({
      type: "genre",
      label: getGenreName(id),
      sub: `公式ジャンル・${count}件`,
      href: `/search?genre=${encodeURIComponent(id)}`,
    }));

  const seen = new Set<string>();
  return [...workSuggestions, ...personSuggestions, ...genreSuggestions]
    .filter((suggestion) => {
      const key = `${suggestion.type}\u0000${suggestion.label}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, limit);
}

/** 補完候補（従来APIとの互換用。UIはリポジトリ経由で取得する） */
export function getSuggestions(query: string, mode: AudienceType, limit = 8): Suggestion[] {
  const pool = allWorks.filter((work) => matchesAudience(work, mode));
  return getSuggestionsFromPool(query, mode, pool, limit);
}

export function getSuggestionTextFromPool(
  query: string,
  mode: AudienceType,
  pool: Work[],
): SearchCorrection | null {
  const trimmed = query.trim();
  if (!trimmed) return null;
  const normalized = normalizeText(trimmed);
  const close = pool
    .map((work) => ({ work, score: scoreWork(work, trimmed.slice(0, Math.max(2, trimmed.length - 1))) }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || a.work.id.localeCompare(b.work.id))[0];
  if (close) return { text: close.work.title, href: workPath(close.work.id) };
  const genre = getGenresForAudience(mode).find((item) => normalizeText(item.name).startsWith(normalized[0]));
  if (genre) return { text: genre.name, href: `/search?genre=${encodeURIComponent(genre.id)}` };
  return null;
}

export function getSuggestionText(query: string, mode: AudienceType): SearchCorrection | null {
  const pool = allWorks.filter((work) => matchesAudience(work, mode));
  return getSuggestionTextFromPool(query, mode, pool);
}

/** 人気スコア・新着・評価からランキング対象を並べる */
export function getRankingWorks(mode: AudienceType, type: RankingType, pool: Work[] = allWorks): Work[] {
  const modePool = pool.filter((work) => matchesAudience(work, mode));
  const dayDiff = (dateString: string) =>
    (SITE_TODAY.getTime() - new Date(dateString).getTime()) / (1000 * 60 * 60 * 24);

  if (type === "fresh") {
    const times = buildTimeMap(modePool).added;
    return modePool
      .slice()
      .sort((a, b) => (times.get(b.id) ?? 0) - (times.get(a.id) ?? 0) || a.id.localeCompare(b.id));
  }

  if (type === "rating") {
    return sortWorks(
      modePool.filter((work) => work.rating !== null),
      "rating",
    );
  }

  return modePool
    .filter((work) => work.rankingScore !== null)
    .map((work) => {
      const base = work.rankingScore || 0;
      let score = base;
      if (type === "daily") score = base + Math.max(0, 8 - dayDiff(work.addedAt));
      if (type === "weekly") score = base + Math.max(0, 6 - dayDiff(work.updatedAt) / 7);
      if (type === "editorial") score = base * 0.6 + (work.rating || 0) * 8;
      return { work, score };
    })
    .sort((a, b) => b.score - a.score || a.work.id.localeCompare(b.work.id))
    .map((entry) => entry.work);
}

export function getRanking(mode: AudienceType, type: RankingType, limit = 10): Work[] {
  return getRankingWorks(mode, type).slice(0, limit);
}

export const RANKING_UPDATED_AT = "2026年10月17日 06:00";